import { Optional } from '@vimeo/core/types/generic';

/**
 * Same as lodash, but with native spread which is faster.
 * Returns new object without provided key.
 */
export function omit<T, K extends keyof T>(target: T, key: K): Omit<T, K> {
  const { [key]: omitted, ...rest } = target;
  return rest;
}

/**
 * Returns new object based on filter function results.
 */
export function filterObject<T>(
  target: T,
  predicate: (key: string, value: any) => boolean,
): Partial<T> {
  return Object.keys(target)
    .filter((key) => predicate(key, target[key]))
    .reduce((res, key) => ((res[key] = target[key]), res), {});
}

/**
 * Returns last element of searched element in an array that meets conditions.
 *
 * @param array - target collection to search for last element.
 * @param predicate - callback to check whether element meets requirements
 * @returns last element matching predicate condition
 */
export function findLast<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: Array<T>) => boolean,
): Optional<T> {
  const lastIndex: number = findLastIndex(array, predicate);
  return lastIndex === -1 ? null : array[lastIndex];
}

/**
 * Returns last index of searched element in an array
 */
export function findLastIndex<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: Array<T>) => boolean,
): number {
  let currentIndex = array.length;

  while (currentIndex--) {
    if (predicate(array[currentIndex], currentIndex, array)) {
      return currentIndex;
    }
  }

  return -1;
}
